import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Switch,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import StorageIcon from '@mui/icons-material/Storage';
import ChatIcon from '@mui/icons-material/Chat';
import MessageIcon from '@mui/icons-material/Message';
import BoltIcon from '@mui/icons-material/Bolt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DownloadIcon from '@mui/icons-material/Download';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTheme } from '@mui/material/styles';
import { Header } from './Header';
import AIAssistantDrawer from './AIAssistantDrawer';

export default function CompanyAdmin({ isDarkMode, setIsDarkMode }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [availableLLMProviders, setAvailableLLMProviders] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Modal states
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Form states
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    name: '',
    email: '',
    subscription_tier: '',
    allowed_upload_domains: '',
    custom_css: '',
  });

  // LLM providers state
  const [llmProviders, setLlmProviders] = useState({});
  const [providerToAdd, setProviderToAdd] = useState('');
  const providersContainerRef = useRef(null);

  // Embedding provider state (dedicated for RAG)
  const [embeddingProvider, setEmbeddingProvider] = useState({
    provider: 'openai',
    api_key: '',
    model: 'text-embedding-3-small',
    enabled: true,
  });

  // Memory LLM provider state (for Mem0 memory extraction)
  const [memoryLlmProvider, setMemoryLlmProvider] = useState('auto');

  // Logo/Favicon upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', message: '' });
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    dateFrom: '',
    dateTo: '',
    customGptId: '',
    userId: '',
  });
  const [companyGpts, setCompanyGpts] = useState([]);

  // AI Assistant drawer state for analyzing conversations
  const [analyzeDrawerOpen, setAnalyzeDrawerOpen] = useState(false);

  const getAuthToken = () => localStorage.getItem('cme_token');

  // Check if current user can export conversations (owner or super admin)
  const canExportConversations = currentUser?.role === 'super_admin' || currentUser?.role === 'company_owner';

  // Load LLM providers
  const loadLLMProviders = useCallback(async () => {
    const token = getAuthToken();
    try {
      const res = await fetch('/companies/config/llm-providers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableLLMProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error loading LLM providers:', error);
      setAvailableLLMProviders([
        { id: 'openai', name: 'OpenAI' },
        { id: 'gemini', name: 'Google Gemini' },
        { id: 'deepseek', name: 'DeepSeek' },
        { id: 'claude', name: 'Anthropic Claude' },
      ]);
    }
  }, []);

  // Load users
  const loadUsers = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  // Load invitations
  const loadInvitations = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/invitations?active_only=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }, []);

  // Load statistics
  const loadStatistics = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${companyId}/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  }, []);

  // Load company GPTs for export filter
  const loadCompanyGpts = useCallback(async (companyId) => {
    const token = getAuthToken();
    try {
      const res = await fetch('/custom-gpts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const gpts = await res.json();
        setCompanyGpts(gpts || []);
      }
    } catch (error) {
      console.error('Error loading GPTs:', error);
    }
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      const token = getAuthToken();
      if (!token) {
        window.location.href = '/';
        return;
      }

      try {
        await loadLLMProviders();

        // Get current user
        const userRes = await fetch('/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!userRes.ok) throw new Error('Failed to get user info');
        const user = await userRes.json();

        if (user.role !== 'company_admin' && user.role !== 'company_owner' && user.role !== 'super_admin') {
          alert('Access denied. This page is for company administrators only.');
          window.location.href = '/';
          return;
        }

        setCurrentUser(user);

        // Get company info
        if (user.company_id) {
          const companyRes = await fetch(`/companies/${user.company_id}/admin`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (companyRes.ok) {
            const company = await companyRes.json();
            setCurrentCompany(company);

            // Set document title and favicon
            document.title = `${company.name} - Admin`;
            if (company.favicon_url) {
              const faviconUrl = `${company.favicon_url}?t=${Date.now()}`;
              const existingFavicon = document.querySelector('link[rel="icon"]');
              if (existingFavicon) {
                existingFavicon.href = faviconUrl;
              } else {
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = faviconUrl;
                document.head.appendChild(link);
              }
            }

            // Set settings form values
            setSettings({
              name: company.name || '',
              email: company.email || '',
              subscription_tier: company.subscription_tier || 'free',
              allowed_upload_domains: (company.allowed_upload_domains || []).join('\n'),
              custom_css: company.custom_css || '',
            });

            // Set LLM providers
            if (company.settings?.providers) {
              setLlmProviders(company.settings.providers);
            }

            // Set embedding provider
            if (company.settings?.embedding_provider) {
              setEmbeddingProvider(company.settings.embedding_provider);
            }

            // Set memory LLM provider
            if (company.settings?.memory_llm_provider) {
              setMemoryLlmProvider(company.settings.memory_llm_provider);
            }

            // Load data
            await Promise.all([
              loadUsers(user.company_id),
              loadInvitations(user.company_id),
              loadStatistics(user.company_id),
              loadCompanyGpts(user.company_id),
            ]);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Initialization error:', error);
        alert('Failed to load page. Please try again.');
        window.location.href = '/';
      }
    };

    init();
  }, [loadLLMProviders, loadUsers, loadInvitations, loadStatistics, loadCompanyGpts]);

  const handleBlockUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to block ${userName}? They will no longer be able to access the system.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/block`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to block user');

      alert(`${userName} has been blocked successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user: ' + error.message);
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to unblock ${userName}?`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/unblock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to unblock user');

      alert(`${userName} has been unblocked successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Failed to unblock user: ' + error.message);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to permanently remove ${userName}? This action cannot be undone.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to remove user');

      alert(`${userName} has been removed successfully.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Failed to remove user: ' + error.message);
    }
  };

  const handleChangeRole = async (userId, userName, newRole) => {
    const roleLabels = {
      user: 'User',
      company_admin: 'Company Admin',
      company_owner: 'Company Owner',
    };

    if (!confirm(`Change ${userName}'s role to ${roleLabels[newRole]}?`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to change role');
      }

      alert(`${userName}'s role has been changed to ${roleLabels[newRole]}.`);
      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error changing role:', error);
      alert('Failed to change role: ' + error.message);
    }
  };

  const handleToggleCanCreateGpts = async (userId, currentValue) => {
    const token = getAuthToken();
    try {
      const res = await fetch(`/users/${userId}/permissions`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ can_create_gpts: !currentValue }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update permission');
      }

      loadUsers(currentUser.company_id);
    } catch (error) {
      console.error('Error updating permission:', error);
      alert('Failed to update permission: ' + error.message);
    }
  };

  // Get available roles for role change based on current user's permissions
  const getAvailableRolesForChange = (targetUser) => {
    // Cannot change own role or super_admin roles
    if (targetUser.id === currentUser?.id || targetUser.role === 'super_admin') {
      return [];
    }

    const allRoles = [
      { value: 'user', label: 'User' },
      { value: 'company_admin', label: 'Company Admin' },
      { value: 'company_owner', label: 'Company Owner' },
    ];

    let availableRoles = [];

    // Super admin and company owner can assign all roles
    if (currentUser?.role === 'super_admin' || currentUser?.role === 'company_owner') {
      availableRoles = [...allRoles];
    }
    // Company admin can only assign user or admin (not owner)
    else if (currentUser?.role === 'company_admin') {
      // Admin cannot change owners at all
      if (targetUser.role === 'company_owner') {
        return [];
      }
      // Admin can only assign user or admin
      availableRoles = allRoles.filter((r) => r.value !== 'company_owner');
    }

    // Filter out the target user's current role (no point changing to the same role)
    return availableRoles.filter((r) => r.value !== targetUser.role);
  };

  // Export conversations (company owner/super admin only)
  const handleExportConversations = async () => {
    if (!canExportConversations) {
      alert('Only Company Owners can export conversations.');
      return;
    }

    setExporting(true);
    const token = getAuthToken();

    try {
      // Build query params from filters
      const params = new URLSearchParams();
      if (exportFilters.dateFrom) params.append('date_from', exportFilters.dateFrom);
      if (exportFilters.dateTo) params.append('date_to', exportFilters.dateTo);
      if (exportFilters.customGptId) params.append('custom_gpt_id', exportFilters.customGptId);
      // For specific user (not "public" group), pass user_id to API
      if (exportFilters.userId && exportFilters.userId !== 'public') {
        params.append('user_id', exportFilters.userId);
      }

      const queryString = params.toString();
      const url = `/companies/${currentUser.company_id}/export/threads${queryString ? `?${queryString}` : ''}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to export conversations');
      }

      let data = await res.json();

      // If "public" is selected, filter to only public users client-side
      if (exportFilters.userId === 'public') {
        data = {
          ...data,
          threads: (data.threads || []).filter(t =>
            t.user_id && t.user_id.toLowerCase().startsWith('public')
          ),
        };
        data.summary = {
          ...data.summary,
          total_threads: data.threads.length,
        };
      }

      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${currentCompany?.name || 'company'}_conversations_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      alert(`Successfully exported ${data.threads?.length || 0} conversations.`);
    } catch (error) {
      console.error('Error exporting conversations:', error);
      alert('Failed to export conversations: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Analyze conversations - open AI Assistant drawer (data fetched server-side)
  const handleAnalyzeConversations = () => {
    if (!canExportConversations) {
      alert('Only Company Owners can analyze conversations.');
      return;
    }
    // Just open the drawer - data will be fetched server-side via contextSource
    setAnalyzeDrawerOpen(true);
  };

  const handleResendInvitation = async (invitationId, email) => {
    if (!confirm(`Resend invitation to ${email}?`)) return;

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${currentUser.company_id}/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to resend invitation');

      const result = await res.json();
      if (result.email_sent) {
        alert(`Invitation resent successfully to ${email}!`);
      } else {
        const fullUrl = `${window.location.origin}/accept-invite.html?token=${result.token}`;
        alert(`Email service not configured.\n\nInvitation link:\n${fullUrl}`);
        navigator.clipboard.writeText(fullUrl);
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation: ' + error.message);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${currentUser.company_id}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to cancel invitation');

      loadInvitations(currentUser.company_id);
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      alert('Failed to cancel invitation');
    }
  };

  const handleSaveSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      const allowedDomains = settings.allowed_upload_domains
        .split('\n')
        .map(d => d.trim())
        .filter(d => d.length > 0);

      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: settings.name,
          email: settings.email,
          allowed_upload_domains: allowedDomains,
          custom_css: settings.custom_css,
        }),
      });

      if (!res.ok) throw new Error('Failed to save settings');

      const company = await res.json();
      setCurrentCompany(company);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to extract model IDs from supported_models (handles both object and string formats)
  const extractModelIds = (supportedModels) => {
    if (!supportedModels) return [];
    return supportedModels.map((m) => (typeof m === 'object' ? m.id : m));
  };

  // Helper to get full model info from config (for checkbox display)
  const getProviderModels = (providerId) => {
    const providerConfig = availableLLMProviders.find((p) => p.id === providerId);
    if (!providerConfig?.supported_models) return [];
    return providerConfig.supported_models.map((m) => {
      if (typeof m === 'object') {
        return { id: m.id, supports_tools: m.supports_tools !== false };
      }
      return { id: m, supports_tools: true }; // backward compat: strings support tools
    });
  };

  // Toggle model selection
  const handleModelToggle = (providerKey, modelId, isChecked) => {
    const currentModels = llmProviders[providerKey]?.available_models || [];
    let newModels;
    if (isChecked) {
      newModels = [...currentModels, modelId];
    } else {
      newModels = currentModels.filter((m) => m !== modelId);
    }
    handleProviderChange(providerKey, 'available_models', newModels);
  };

  // LLM Provider handlers
  const handleAddProvider = () => {
    if (!providerToAdd) return;

    const providerConfig = availableLLMProviders.find((p) => p.id === providerToAdd);
    const modelIds = extractModelIds(providerConfig?.supported_models);
    let providerKey = providerToAdd;
    let counter = 1;
    while (llmProviders[providerKey]) {
      counter++;
      providerKey = `${providerToAdd}_${counter}`;
    }

    // Check if this is a custom provider
    const isCustomProvider = providerToAdd === 'custom' || providerConfig?.is_custom;

    // Base config for all providers
    const newConfig = {
      name: providerConfig?.name || providerToAdd,
      provider: providerToAdd,
      enabled: true,
      api_key: '',
      default_model: isCustomProvider ? '' : (modelIds[0] || ''),
      available_models: isCustomProvider ? [] : modelIds,
      embedding_model: providerConfig?.embedding_models?.[0] || '',
      is_default: Object.keys(llmProviders).length === 0,
    };

    // Add custom provider specific fields
    if (isCustomProvider) {
      newConfig.base_url = '';
      newConfig.supports_tools = true;
      newConfig.supports_vision = false;
    }

    setLlmProviders((prev) => ({
      ...prev,
      [providerKey]: newConfig,
    }));
    setProviderToAdd('');

    setTimeout(() => {
      if (providersContainerRef.current) {
        const lastChild = providersContainerRef.current.lastElementChild;
        if (lastChild) {
          lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const handleRemoveProvider = (key) => {
    setLlmProviders((prev) => {
      const newProviders = { ...prev };
      delete newProviders[key];
      return newProviders;
    });
  };

  const handleProviderChange = (key, field, value) => {
    setLlmProviders((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSetDefault = (key) => {
    setLlmProviders((prev) => {
      const newProviders = {};
      Object.keys(prev).forEach((k) => {
        newProviders[k] = { ...prev[k], is_default: k === key };
      });
      return newProviders;
    });
  };

  const handleSaveLLMSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            providers: Object.keys(llmProviders).length > 0 ? llmProviders : null,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to save LLM settings');

      alert('LLM configuration saved successfully!');
    } catch (error) {
      console.error('Error saving LLM settings:', error);
      alert('Failed to save LLM settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Save embedding provider settings
  const handleSaveEmbeddingSettings = async () => {
    const token = getAuthToken();
    setSubmitting(true);

    try {
      const res = await fetch(`/companies/${currentUser.company_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: {
            embedding_provider: embeddingProvider,
            memory_llm_provider: memoryLlmProvider,
          },
        }),
      });

      if (!res.ok) throw new Error('Failed to save RAG & Memory settings');

      alert('RAG & Memory configuration saved successfully!');
    } catch (error) {
      console.error('Error saving RAG & Memory settings:', error);
      alert('Failed to save RAG & Memory settings');
    } finally {
      setSubmitting(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadMessage({ type: 'error', message: 'Logo file size must be less than 5MB' });
      return;
    }

    const token = getAuthToken();
    setLogoUploading(true);
    setUploadMessage({ type: '', message: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`/companies/${currentUser.company_id}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to upload logo');
      }

      const result = await res.json();
      setCurrentCompany(result.company);
      setUploadMessage({ type: 'success', message: 'Logo uploaded successfully!' });

      // Clear file input
      if (logoInputRef.current) logoInputRef.current.value = '';
    } catch (error) {
      console.error('Logo upload error:', error);
      setUploadMessage({ type: 'error', message: `Failed to upload logo: ${error.message}` });
    } finally {
      setLogoUploading(false);
    }
  };

  // Favicon upload handler
  const handleFaviconUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      setUploadMessage({ type: 'error', message: 'Favicon file size must be less than 500KB' });
      return;
    }

    const token = getAuthToken();
    setFaviconUploading(true);
    setUploadMessage({ type: '', message: '' });

    try {
      const formData = new FormData();
      formData.append('favicon', file);

      const res = await fetch(`/companies/${currentUser.company_id}/favicon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to upload favicon');
      }

      const result = await res.json();
      setCurrentCompany(result.company);
      setUploadMessage({ type: 'success', message: 'Favicon uploaded successfully!' });

      // Clear file input
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    } catch (error) {
      console.error('Favicon upload error:', error);
      setUploadMessage({ type: 'error', message: `Failed to upload favicon: ${error.message}` });
    } finally {
      setFaviconUploading(false);
    }
  };

  const isImpersonating = localStorage.getItem('cme_impersonating') === 'true';

  const handleReturnToSuperAdmin = () => {
    const originalToken = localStorage.getItem('cme_original_token');
    if (originalToken) {
      localStorage.setItem('cme_token', originalToken);
      localStorage.removeItem('cme_original_token');
      localStorage.removeItem('cme_impersonating');
      window.location.href = '/super-admin.html';
    }
  };

  const handleToggleTheme = async () => {
    const newTheme = isDarkMode ? 'light' : 'dark';
    setIsDarkMode(!isDarkMode);

    // Update document class
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Update localStorage
    const userData = localStorage.getItem('cme_user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        user.theme = newTheme;
        localStorage.setItem('cme_user', JSON.stringify(user));
      } catch (e) {
        // ignore
      }
    }

    // Save to backend
    const token = getAuthToken();
    try {
      await fetch('/users/me', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme: newTheme }),
      });
    } catch (e) {
      console.error('Failed to save theme preference:', e);
    }

    // Dispatch event for other components
    window.dispatchEvent(new Event('themeChanged'));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Header
        company={currentCompany}
        isImpersonating={isImpersonating}
        onReturnToSuperAdmin={handleReturnToSuperAdmin}
        isDarkMode={isDarkMode}
        onToggleTheme={handleToggleTheme}
        userRole={currentUser?.role}
        contentMaxWidth="1400px"
        contentPadding="24px"
        logoMinWidth="1990px"
      />

      <Box className="admin-container">
        {/* Page Header */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            Company Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, settings, and view company statistics
          </Typography>
        </Box>
        <Divider sx={{ mb: 4 }} />

        {/* User Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Your Account
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Username</Typography>
                <Typography fontWeight={500}>{currentUser?.username}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography fontWeight={500}>{currentUser?.email}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Chip
                  label={currentUser?.role?.replace('_', ' ').toUpperCase()}
                  size="small"
                  color={
                    currentUser?.role === 'company_owner' ? 'secondary' :
                    currentUser?.role === 'company_admin' ? 'primary' : 'default'
                  }
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Statistics" icon={<BarChartIcon />} iconPosition="start" />
          <Tab label="Users" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Invitations" icon={<PersonAddIcon />} iconPosition="start" />
          <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="LLM Configuration" icon={<SmartToyIcon />} iconPosition="start" />
          <Tab label="RAG & Memory" icon={<DataObjectIcon />} iconPosition="start" />
          {canExportConversations && (
            <Tab label="Reports" icon={<DownloadIcon />} iconPosition="start" />
          )}
        </Tabs>

        {/* Statistics Tab */}
        {activeTab === 0 && statistics && (
          <Box>
            {/* Stats Cards - Gentle styling with circular pastel icons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 26, color: '#3b82f6' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Total Users</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.users.total}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.users.limit} limit</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 26, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Active Users (7d)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.users.active_last_7_days}</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 26, color: '#a855f7' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Conversations</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.threads.total.toLocaleString()}</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(14, 165, 233, 0.2)' : '#e0f2fe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <MessageIcon sx={{ fontSize: 26, color: '#0ea5e9' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Total Messages</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.messages.total.toLocaleString()}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 26, color: '#f59e0b' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Tokens (Month)</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.tokens.total_this_month.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {statistics.tokens.limit_per_month
                        ? `${statistics.tokens.percentage_used?.toFixed(1) || 0}% of ${statistics.tokens.limit_per_month.toLocaleString()}`
                        : 'Unlimited'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 26, color: '#ef4444' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Assistants</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.custom_gpts.total}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.custom_gpts.limit} limit</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(107, 114, 128, 0.2)' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <StorageIcon sx={{ fontSize: 26, color: '#6b7280' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Storage Used</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.storage.used_mb} MB</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>of {statistics.storage.limit_mb} MB ({statistics.storage.percentage_used}%)</Typography>
                  </Box>
                </CardContent>
              </Card>
              <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      bgcolor: isDarkMode ? 'rgba(236, 72, 153, 0.2)' : '#fce7f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 26, color: '#ec4899' }} />
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>Avg Msgs/Thread</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{statistics.messages.average_per_thread}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Top Users & GPTs */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4 }}>
              <Card sx={{ flex: 1, minWidth: 300 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Active Users</Typography>
                  {statistics.top_users.by_messages.length === 0 ? (
                    <Typography color="text.secondary">No activity yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {statistics.top_users.by_messages.map((user, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Box>
                              <Typography fontWeight={500}>{user.display_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{user.message_count}</Typography>
                            <Typography variant="caption" color="text.secondary">messages</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 300 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Used Assistants</Typography>
                  {statistics.custom_gpts.most_used.length === 0 ? (
                    <Typography color="text.secondary">No assistants used yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {statistics.custom_gpts.most_used.map((gpt, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Typography fontWeight={500}>{gpt.name}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{gpt.thread_count}</Typography>
                            <Typography variant="caption" color="text.secondary">conversations</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {/* Users Tab */}
        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Company Users</Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteModalOpen(true)}
                  sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Invite User
                  </Box>
                </Button>
              </Box>

              {users.length === 0 ? (
                <Typography color="text.secondary">No users found</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {users.map((user) => {
                    const isCurrentUser = user.id === currentUser?.id;
                    const isSuperAdmin = user.role === 'super_admin';
                    const canManage = !isCurrentUser && !isSuperAdmin;

                    return (
                      <Box
                        key={user.id}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 200 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography fontWeight={600}>{user.display_name || user.email}</Typography>
                            <Chip
                              label={user.role.replace('_', ' ').toUpperCase()}
                              size="small"
                              color={
                                user.role === 'company_owner' ? 'secondary' :
                                user.role === 'company_admin' ? 'primary' : 'default'
                              }
                            />
                            {isCurrentUser && <Chip label="You" size="small" variant="outlined" />}
                          </Box>
                          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.last_login
                              ? `Last login: ${new Date(user.last_login).toLocaleString()}`
                              : 'Never logged in'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          {/* Can Create GPTs toggle - only for regular users (admins/owners always can) */}
                          {user.role === 'user' && canManage && (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={user.can_create_gpts || false}
                                  onChange={() => handleToggleCanCreateGpts(user.id, user.can_create_gpts)}
                                />
                              }
                              label="Can create assistants"
                              sx={{ mr: 0 }}
                            />
                          )}
                          <Chip
                            label={user.is_active ? 'Active' : 'Blocked'}
                            size="small"
                            color={user.is_active ? 'success' : 'error'}
                          />
                          {canManage && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              {/* Role Change Dropdown */}
                              {getAvailableRolesForChange(user).length > 0 && (
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value=""
                                    displayEmpty
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleChangeRole(user.id, user.display_name || user.email, e.target.value);
                                      }
                                    }}
                                    renderValue={() => (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                        <EditIcon fontSize="small" />
                                        <span>Change Role</span>
                                      </Box>
                                    )}
                                  >
                                    {getAvailableRolesForChange(user).map((role) => (
                                      <MenuItem key={role.value} value={role.value}>
                                        {role.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                              {user.is_active ? (
                                <Button
                                  size="small"
                                  color="warning"
                                  startIcon={<BlockIcon />}
                                  onClick={() => handleBlockUser(user.id, user.display_name || user.email)}
                                >
                                  Block
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  color="success"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleUnblockUser(user.id, user.display_name || user.email)}
                                >
                                  Unblock
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleRemoveUser(user.id, user.display_name || user.email)}
                              >
                                Remove
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Invitations Tab */}
        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Pending Invitations</Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setInviteModalOpen(true)}
                  sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1, sm: 2 } }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Invite User
                  </Box>
                </Button>
              </Box>

              {invitations.length === 0 ? (
                <Typography color="text.secondary">No pending invitations</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {invitations.map((inv) => (
                    <Box
                      key={inv.id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography fontWeight={600}>{inv.email}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {inv.role.replace('_', ' ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires: {new Date(inv.expires_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<SendIcon />}
                          onClick={() => handleResendInvitation(inv.id, inv.email)}
                        >
                          Resend
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancelInvitation(inv.id)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 3 && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>Company Settings</Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings.name}
                  onChange={(e) => setSettings((prev) => ({ ...prev, name: e.target.value }))}
                />
                <TextField
                  fullWidth
                  label="Company Email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings((prev) => ({ ...prev, email: e.target.value }))}
                />
                <TextField
                  fullWidth
                  label="Subscription Tier"
                  value={settings.subscription_tier}
                  disabled
                  helperText="Contact support to change subscription tier"
                />
                <TextField
                  fullWidth
                  label="Allowed Upload Domains"
                  value={settings.allowed_upload_domains}
                  onChange={(e) => setSettings((prev) => ({ ...prev, allowed_upload_domains: e.target.value }))}
                  multiline
                  rows={3}
                  placeholder="yourdomain.com&#10;your-bucket.s3.amazonaws.com"
                  helperText="One domain per line. These domains are trusted for URL-based document uploads."
                />
                <TextField
                  fullWidth
                  label="Custom CSS"
                  value={settings.custom_css}
                  onChange={(e) => setSettings((prev) => ({ ...prev, custom_css: e.target.value }))}
                  multiline
                  rows={6}
                  placeholder="nav.bg-white { background-color: #your-color; }"
                  helperText="Add custom CSS to style your pages."
                  sx={{ fontFamily: 'monospace' }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={submitting}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save Settings'}
                </Button>
              </Box>

              {/* Company Logo Section */}
              <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Company Logo</Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {/* Logo Preview */}
                  <Box
                    sx={{
                      width: 128,
                      height: 128,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                    }}
                  >
                    {currentCompany?.logo_url ? (
                      <img
                        src={`${currentCompany.logo_url}?t=${Date.now()}`}
                        alt="Company logo"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">No logo</Typography>
                    )}
                  </Box>

                  {/* Upload Form */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Upload Logo</Typography>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                      id="logo-upload-input"
                    />
                    <label htmlFor="logo-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={logoUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        disabled={logoUploading}
                      >
                        {logoUploading ? 'Uploading...' : 'Choose File'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Supported formats: PNG, JPG, GIF, WebP (max 5MB, recommended: 500x500px)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Company Favicon Section */}
              <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Company Favicon</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The favicon is the small icon displayed in browser tabs and bookmarks
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {/* Favicon Preview */}
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                    }}
                  >
                    {currentCompany?.favicon_url ? (
                      <img
                        src={`${currentCompany.favicon_url}?t=${Date.now()}`}
                        alt="Favicon"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">No favicon</Typography>
                    )}
                  </Box>

                  {/* Upload Form */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} gutterBottom>Upload Favicon</Typography>
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/png,image/x-icon,.ico,.png"
                      onChange={handleFaviconUpload}
                      style={{ display: 'none' }}
                      id="favicon-upload-input"
                    />
                    <label htmlFor="favicon-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={faviconUploading ? <CircularProgress size={16} /> : <ImageIcon />}
                        disabled={faviconUploading}
                      >
                        {faviconUploading ? 'Uploading...' : 'Choose File'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Supported formats: PNG, ICO (max 500KB, recommended: 32x32px or 64x64px)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Upload Message */}
              {uploadMessage.message && (
                <Alert severity={uploadMessage.type === 'error' ? 'error' : 'success'} sx={{ mt: 3 }}>
                  {uploadMessage.message}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* LLM Configuration Tab */}
        {activeTab === 4 && (
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>LLM Provider Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure the AI models and providers for your company
                </Typography>
              </Box>

              {/* LLM Providers Section */}
              <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1.5, sm: 0 },
                  mb: 2
                }}>
                  <Typography variant="h6" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    LLM Providers
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
                      <Select
                        value={providerToAdd}
                        onChange={(e) => setProviderToAdd(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Select provider...</MenuItem>
                        {availableLLMProviders.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddProvider}
                      startIcon={<AddIcon />}
                      sx={{ minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 1.5, sm: 2 } }}
                    >
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        Add
                      </Box>
                    </Button>
                  </Box>
                </Box>

                {Object.keys(llmProviders).length === 0 ? (
                  <Typography color="text.secondary" fontStyle="italic">
                    No providers configured. Select a provider above and click "Add" to configure one.
                  </Typography>
                ) : (
                  <Box ref={providersContainerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(llmProviders).map(([key, config]) => (
                      <Box
                        key={key}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          bgcolor: config.is_default
                            ? (isDarkMode ? 'rgba(37, 99, 235, 0.15)' : '#eff6ff')
                            : (isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                          border: '1px solid',
                          borderColor: config.is_default
                            ? (isDarkMode ? 'rgba(147, 197, 253, 0.5)' : '#93c5fd')
                            : 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {config.name || key}
                              {' '}
                              <Typography component="span" variant="subtitle1" color="text.secondary" fontWeight={400}>
                                ({availableLLMProviders.find(p => p.id === config.provider)?.name || config.provider || key.split('_')[0]})
                              </Typography>
                            </Typography>
                            {config.is_default && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{ bgcolor: '#2563eb', color: 'white', fontSize: '0.75rem', height: 22 }}
                              />
                            )}
                          </Box>
                          <IconButton size="small" color="error" onClick={() => handleRemoveProvider(key)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 3 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={config.enabled}
                                  onChange={(e) => handleProviderChange(key, 'enabled', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Enabled</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={config.is_default}
                                  onChange={() => handleSetDefault(key)}
                                  size="small"
                                />
                              }
                              label={<Typography variant="body2">Set as Default</Typography>}
                            />
                          </Box>

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Name</Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={config.name || ''}
                              onChange={(e) => handleProviderChange(key, 'name', e.target.value)}
                              placeholder="e.g., Production, Development, Testing"
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Custom name shown to users when creating assistants
                            </Typography>
                          </Box>

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                              API Key {config.provider === 'custom' && <Typography component="span" variant="caption" color="text.secondary">(optional for local models)</Typography>}
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              type="password"
                              value={config.api_key || ''}
                              onChange={(e) => handleProviderChange(key, 'api_key', e.target.value)}
                              placeholder={config.provider === 'custom' ? 'Optional - leave empty for local models' : 'API key'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                          </Box>

                          {/* Custom Provider Fields */}
                          {config.provider === 'custom' && (
                            <>
                              <Box>
                                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Base URL *</Typography>
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={config.base_url || ''}
                                  onChange={(e) => handleProviderChange(key, 'base_url', e.target.value)}
                                  placeholder="http://localhost:11434 (Ollama), http://localhost:8000 (vLLM)"
                                  sx={{ bgcolor: 'background.paper' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  The base URL of your LLM server (Ollama, vLLM, LM Studio, etc.)
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 3 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={config.supports_tools !== false}
                                      onChange={(e) => handleProviderChange(key, 'supports_tools', e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label={<Typography variant="body2">Supports Function Calling (Tools)</Typography>}
                                />
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={config.supports_vision === true}
                                      onChange={(e) => handleProviderChange(key, 'supports_vision', e.target.checked)}
                                      size="small"
                                    />
                                  }
                                  label={<Typography variant="body2">Supports Vision (Images)</Typography>}
                                />
                              </Box>
                            </>
                          )}

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Default Model</Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={config.default_model || ''}
                              onChange={(e) => handleProviderChange(key, 'default_model', e.target.value)}
                              placeholder={config.provider === 'custom'
                                ? 'e.g., ollama/llama3, ollama/mistral, openai/gpt-4 (for Azure)'
                                : 'e.g., gpt-4o-mini, gemini-pro, claude-sonnet-4-5'}
                              sx={{ bgcolor: 'background.paper' }}
                            />
                            {config.provider === 'custom' && (
                              <Typography variant="caption" color="text.secondary">
                                Format: provider/model-name (e.g., ollama/llama3, vllm/mistral-7b, azure/gpt-4)
                              </Typography>
                            )}
                          </Box>

                          <Box>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>Available Models</Typography>
                            {config.provider === 'custom' ? (
                              /* Custom provider: manual model entry with chips */
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', minHeight: 42 }}>
                                  {(config.available_models || []).map((model, idx) => (
                                    <Chip
                                      key={idx}
                                      label={model}
                                      size="small"
                                      onDelete={() => {
                                        const newModels = (config.available_models || []).filter((_, i) => i !== idx);
                                        handleProviderChange(key, 'available_models', newModels);
                                      }}
                                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                    />
                                  ))}
                                  <TextField
                                    size="small"
                                    variant="standard"
                                    placeholder={config.available_models?.length ? "Add model..." : "Type model name and press Enter"}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ',') {
                                        e.preventDefault();
                                        const value = e.target.value.trim();
                                        if (value && !(config.available_models || []).includes(value)) {
                                          handleProviderChange(key, 'available_models', [...(config.available_models || []), value]);
                                          e.target.value = '';
                                        }
                                      }
                                    }}
                                    sx={{
                                      flex: 1,
                                      minWidth: 150,
                                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                                      '& .MuiInput-underline:hover:before': { borderBottom: 'none' },
                                      '& .MuiInput-underline:after': { borderBottom: 'none' },
                                    }}
                                    InputProps={{ disableUnderline: true }}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Type a model name and press Enter to add. Format: provider/model-name (e.g., ollama/llama3)
                                </Typography>
                              </Box>
                            ) : (
                              /* Standard provider: checkbox list */
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto', p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                {getProviderModels(config.provider || key.split('_')[0]).map((model) => (
                                  <FormControlLabel
                                    key={model.id}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={(config.available_models || []).includes(model.id)}
                                        onChange={(e) => handleModelToggle(key, model.id, e.target.checked)}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{model.id}</Typography>
                                        {!model.supports_tools && (
                                          <Chip label="No Tools" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        )}
                                      </Box>
                                    }
                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                                  />
                                ))}
                                {getProviderModels(config.provider || key.split('_')[0]).length === 0 && (
                                  <Typography variant="caption" color="text.secondary">No models configured for this provider</Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveLLMSettings}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save LLM Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* RAG & Memory Tab */}
        {activeTab === 5 && (
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>RAG & Embedding Configuration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure a dedicated embedding provider for RAG (Retrieval-Augmented Generation).
                  This provider is used for all document embeddings and queries, ensuring consistency
                  regardless of which LLM provider is used for chat.
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> All documents in your knowledge bases will be embedded using this provider.
                  If you change the embedding provider or model, you will need to re-upload your documents
                  for RAG to work correctly.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={embeddingProvider.enabled}
                      onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                  }
                  label="Enable RAG / Embeddings"
                />

                <FormControl fullWidth>
                  <InputLabel>Embedding Provider</InputLabel>
                  <Select
                    value={embeddingProvider.provider}
                    label="Embedding Provider"
                    onChange={(e) => {
                      const provider = e.target.value;
                      let defaultModel = 'text-embedding-3-small';
                      if (provider === 'gemini') defaultModel = 'gemini-embedding-001';
                      else if (provider === 'deepseek') defaultModel = 'deepseek-embedding-v3';
                      setEmbeddingProvider(prev => ({ ...prev, provider, model: defaultModel }));
                    }}
                  >
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Google Gemini</MenuItem>
                    <MenuItem value="deepseek">DeepSeek</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="API Key"
                  type="password"
                  value={embeddingProvider.api_key}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="API key for embedding provider"
                  helperText="This can be the same or different from your LLM provider API key"
                />

                <TextField
                  fullWidth
                  label="Embedding Model"
                  value={embeddingProvider.model}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, model: e.target.value }))}
                  placeholder={
                    embeddingProvider.provider === 'openai' ? 'text-embedding-3-small' :
                    embeddingProvider.provider === 'gemini' ? 'gemini-embedding-001' :
                    embeddingProvider.provider === 'deepseek' ? 'deepseek-embedding-v3' :
                    'Embedding model name'
                  }
                  helperText={
                    embeddingProvider.provider === 'openai' ? 'Options: text-embedding-3-small, text-embedding-3-large, text-embedding-ada-002' :
                    embeddingProvider.provider === 'gemini' ? 'Options: gemini-embedding-001' :
                    embeddingProvider.provider === 'deepseek' ? 'Options: deepseek-embedding-v3' :
                    'Enter the embedding model name'
                  }
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Memory LLM Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure which LLM provider to use for memory extraction (Mem0).
                  This is used when assistants have learning mode enabled.
                </Typography>

                {!embeddingProvider.enabled && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Memory requires RAG / Embeddings to be enabled. Enable it above to use memory features.
                  </Alert>
                )}

                <FormControl fullWidth sx={{ mb: 2 }} disabled={!embeddingProvider.enabled}>
                  <InputLabel>Memory LLM Provider</InputLabel>
                  <Select
                    value={memoryLlmProvider.split('/')[0] || memoryLlmProvider}
                    label="Memory LLM Provider"
                    onChange={(e) => {
                      const providerKey = e.target.value;
                      if (providerKey === 'auto') {
                        setMemoryLlmProvider('auto');
                      } else {
                        // Get provider config and find full provider definition for models
                        const companyProviderConfig = llmProviders[providerKey] || {};
                        const providerType = companyProviderConfig.provider || providerKey.split('_')[0];
                        const fullProviderConfig = availableLLMProviders.find(p => p.id === providerType);
                        const supportedModels = fullProviderConfig?.supported_models || [];
                        const models = supportedModels.map(m => typeof m === 'object' ? m.id : m);
                        // Pick first model as default (cheapest should be first)
                        const defaultModel = models.length > 0 ? models[0] : '';
                        setMemoryLlmProvider(defaultModel ? `${providerKey}/${defaultModel}` : providerKey);
                      }
                    }}
                  >
                    <MenuItem value="auto">Auto (cheapest available)</MenuItem>
                    {Object.entries(llmProviders || {}).map(([key, config]) => {
                      const providerConfig = config || {};
                      const isEnabled = providerConfig.enabled !== false;
                      if (!isEnabled) return null;
                      const displayName = providerConfig.name || key.charAt(0).toUpperCase() + key.slice(1);
                      return (
                        <MenuItem key={key} value={key}>
                          {displayName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                {/* Model selection when a specific provider is selected */}
                {memoryLlmProvider !== 'auto' && (() => {
                  const selectedProviderKey = memoryLlmProvider.split('/')[0];
                  // Get provider info from the loaded llmProviders config
                  const companyProviderConfig = llmProviders[selectedProviderKey] || {};
                  // Get the actual provider type (e.g., 'gemini' from a provider named 'gemini_2')
                  const providerType = companyProviderConfig.provider || selectedProviderKey.split('_')[0];
                  // Find the full provider config from availableLLMProviders to get supported_models
                  const fullProviderConfig = availableLLMProviders.find(p => p.id === providerType);
                  // Get all supported models from the provider definition
                  const supportedModels = fullProviderConfig?.supported_models || [];
                  // Extract just the model IDs
                  const models = supportedModels.map(m => typeof m === 'object' ? m.id : m);

                  return models.length > 0 ? (
                    <FormControl fullWidth disabled={!embeddingProvider.enabled}>
                      <InputLabel>Memory Model</InputLabel>
                      <Select
                        value={memoryLlmProvider.includes('/') ? memoryLlmProvider.split('/')[1] : ''}
                        label="Memory Model"
                        onChange={(e) => {
                          setMemoryLlmProvider(`${selectedProviderKey}/${e.target.value}`);
                        }}
                      >
                        {models.map((modelId, index) => (
                          <MenuItem key={modelId} value={modelId}>
                            {modelId}{index === 0 ? ' (recommended for memory)' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null;
                })()}

                <Button
                  variant="contained"
                  onClick={handleSaveEmbeddingSettings}
                  disabled={submitting}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save RAG & Memory Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Reports Tab - Only for Company Owners */}
        {activeTab === 6 && canExportConversations && (
          <Card>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Reports & Data Export</Typography>
                <Typography variant="body2" color="text.secondary">
                  Export company data for analysis, compliance, or backup purposes.
                  Only Company Owners have access to this feature.
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Privacy Notice:</strong> Exported conversations contain all user messages and AI responses.
                  Handle this data in accordance with your company's privacy policy and applicable regulations.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Export Conversations Card */}
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: isDarkMode ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <ChatIcon sx={{ fontSize: 24, color: '#a855f7' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight={600}>Export Conversations</Typography>
                          {statistics && (
                            <Typography variant="body2" color="text.secondary">
                              ({statistics.threads?.total?.toLocaleString() || 0} total)
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Download conversations from your company including user messages, AI responses,
                          timestamps, and associated assistant information. Exported as JSON format.
                        </Typography>

                        {/* Filters */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3, maxWidth: 500 }}>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            Filter Options (optional)
                          </Typography>

                          {/* Date Range */}
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                              label="From Date"
                              type="date"
                              size="small"
                              value={exportFilters.dateFrom}
                              onChange={(e) => setExportFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                              sx={{ flex: 1, minWidth: 150 }}
                            />
                            <TextField
                              label="To Date"
                              type="date"
                              size="small"
                              value={exportFilters.dateTo}
                              onChange={(e) => setExportFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                              sx={{ flex: 1, minWidth: 150 }}
                            />
                          </Box>

                          {/* Assistant Selection */}
                          <FormControl size="small" fullWidth>
                            <InputLabel>Assistant</InputLabel>
                            <Select
                              value={exportFilters.customGptId}
                              label="Assistant"
                              onChange={(e) => setExportFilters(prev => ({ ...prev, customGptId: e.target.value }))}
                            >
                              <MenuItem value="">
                                <em>All Assistants</em>
                              </MenuItem>
                              {companyGpts.map((gpt) => (
                                <MenuItem key={gpt.id} value={gpt.id}>
                                  {gpt.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* User Selection */}
                          <FormControl size="small" fullWidth>
                            <InputLabel>User</InputLabel>
                            <Select
                              value={exportFilters.userId}
                              label="User"
                              onChange={(e) => setExportFilters(prev => ({ ...prev, userId: e.target.value }))}
                            >
                              <MenuItem value="">
                                <em>All Users</em>
                              </MenuItem>
                              <MenuItem value="public">
                                <em>Public Users (grouped)</em>
                              </MenuItem>
                              {users.map((user) => (
                                <MenuItem key={user.id} value={user.id}>
                                  {user.display_name || user.email}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* Clear filters button */}
                          {(exportFilters.dateFrom || exportFilters.dateTo || exportFilters.customGptId || exportFilters.userId) && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExportFilters({ dateFrom: '', dateTo: '', customGptId: '', userId: '' })}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Clear Filters
                            </Button>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                            onClick={handleExportConversations}
                            disabled={exporting}
                          >
                            {exporting ? 'Exporting...' : 'Export'}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={handleAnalyzeConversations}
                            disabled={exporting}
                            color="secondary"
                          >
                            Analyze with AI
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        companyId={currentUser?.company_id}
        currentUser={currentUser}
        onSuccess={() => loadInvitations(currentUser?.company_id)}
      />

      {/* AI Assistant Drawer for Conversation Analysis */}
      <AIAssistantDrawer
        open={analyzeDrawerOpen}
        onClose={() => setAnalyzeDrawerOpen(false)}
        title="Conversation Analyzer"
        subtitle={`Analyzing conversations from ${currentCompany?.name || 'your company'}`}
        initialMessage={
          `I'll analyze your company's conversations when you send your first message.\n\n` +
          `${exportFilters.dateFrom ? `Date range: ${exportFilters.dateFrom} to ${exportFilters.dateTo || 'now'}\n` : ''}` +
          `${exportFilters.customGptId ? `Filtered by specific assistant\n` : ''}` +
          `\nAsk me anything about your data! For example:\n` +
          `• "What are the most common topics discussed?"\n` +
          `• "Show me a summary of conversation activity"\n` +
          `• "Which assistant is used most frequently?"\n` +
          `• "Are there any patterns in user questions?"`
        }
        systemPrompt={`You are a data analyst assistant helping analyze conversation data from ${currentCompany?.name || 'a company'}. The attached JSON file contains conversation history. Help the user understand patterns, trends, and insights from this data. Be concise and use specific numbers when possible.`}
        contextSource={{
          type: "export_threads",
          filters: {
            custom_gpt_id: exportFilters.customGptId || null,
            date_from: exportFilters.dateFrom || null,
            date_to: exportFilters.dateTo || null
          }
        }}
        resultLabel="Analysis Result"
        useButtonText="Copy to Clipboard"
        onUseResult={(result) => {
          navigator.clipboard.writeText(result);
          alert('Result copied to clipboard!');
        }}
      />
    </Box>
  );
}

// Invite User Modal Component
function InviteUserModal({ open, onClose, companyId, currentUser, onSuccess }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [canCreateGpts, setCanCreateGpts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Check if current user can invite owners (only owners and super_admin can)
  const canInviteOwner = currentUser?.role === 'company_owner' || currentUser?.role === 'super_admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    const token = localStorage.getItem('cme_token');
    try {
      const res = await fetch(`/companies/${companyId}/invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role, can_create_gpts: role === 'user' ? canCreateGpts : true }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to send invitation');
      }

      const data = await res.json();

      if (data.email_sent) {
        setResult({ success: true, message: `Invitation sent to ${email}!` });
      } else {
        const fullUrl = `${window.location.origin}/accept-invite.html?token=${data.invitation.token}`;
        setResult({
          success: true,
          message: 'Email service not configured. Copy the link below:',
          link: fullUrl,
        });
        navigator.clipboard.writeText(fullUrl);
      }

      setEmail('');
      setRole('user');
      setCanCreateGpts(false);
      onSuccess();
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Invite User
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            placeholder="user@example.com"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="company_admin">Company Admin</MenuItem>
              {canInviteOwner && <MenuItem value="company_owner">Company Owner</MenuItem>}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {role === 'company_owner'
              ? 'Company Owners can export conversations, manage admins and other owners'
              : role === 'company_admin'
              ? 'Company Admins can manage users, invitations, and company settings'
              : 'Users can use assistants assigned to them'}
          </Typography>

          {role === 'user' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={canCreateGpts}
                  onChange={(e) => setCanCreateGpts(e.target.checked)}
                />
              }
              label="Allow user to create assistants"
              sx={{ mt: 1, display: 'block' }}
            />
          )}

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.message}
              {result.link && (
                <Box sx={{ mt: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {result.link}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
